/**
 * 一手结束后的两块 UI：舞台顶部的**结算卡**，和 dock 区的**下一手**按钮。
 *
 * 为什么分成两块：结算卡必须让开屏幕下半部 —— 摊牌那一刻最该看清的就是自己的两张底牌，
 * 而底牌固定在舞台底沿。把卡片压在上面等于「刚亮出来就被挡住」。
 * 行动按钮则天生属于 dock：玩家整局都在那个位置按按钮，结束时不该换地方。
 *
 * 为什么不自动开下一手：本地传手机模式下手牌结束时遮罩会揭开，几个人正凑在一块屏幕前看结果。
 * 固定间隔自己翻页，读慢一点的人就错过了全部信息，而且下一手一开局就立刻遮屏。
 * 所以 AppRoot 传 `autoNextHand: false`，翻页由这里的按钮触发（见 deviations）。
 *
 * 出现时机延后到推池动画之后：筹码还在飞的时候弹卡片会把飞行终点挡住。
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { PublicHandState } from '@poker/shared'

import { MOTION } from '../fx/motion.js'
import { leaveLocalGame, nextLocalHand, useLocalSessionStore } from '../local/localSession.js'
import { selectView, useTableStore, useUiStore, type TableSeatInfo } from '../store/index.js'
import { formatChips, handLabel } from '../table/cardFormat.js'
import { Card } from '../table/Card.js'
import { checkChipConservation, compareShowdown, summarizeHand, type HandSummary } from './handSummary.js'
import './session.css'

/** 推池（420ms）+ 多赢家错峰 + 一点余量。与 TableScene 的 useSettlementClear 同源。 */
function settleDelayMs(winnerCount: number): number {
  return MOTION.push.duration + MOTION.push.gap * Math.max(0, winnerCount - 1) + 240
}

interface Settled {
  readonly summary: HandSummary | null
  /** 推池动画已播完，可以显示结算信息了 */
  readonly shown: boolean
  /** 还有筹码的人不足 2 个，本局到此为止 */
  readonly ended: boolean
}

/**
 * 结算态。两个组件各调一次：它们的延时来自同一个纯函数，不需要共享一份 state
 * （也就不需要再给 App 加一个只为传这一个布尔值的 Context）。
 */
function useSettledHand(): Settled {
  const view = useTableStore(selectView)
  const seats = useTableStore((s) => s.seats)
  const status = useLocalSessionStore((s) => s.status)
  const showToast = useUiStore((s) => s.showToast)

  const summary = useMemo(() => summarizeHand(view, seats), [view, seats])
  const handId = view?.result?.handId ?? null
  // summary 每次 seats 变化都会换引用；定时器只该跟着「手牌身份」和赢家人数走，
  // 否则改个昵称就把卡片的出现时间推后一轮。
  const winnerCount = summary?.winners.length ?? 0
  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (handId === null) {
      setShown(false)
      return undefined
    }
    const t = setTimeout(() => setShown(true), settleDelayMs(winnerCount))
    return () => clearTimeout(t)
  }, [handId, winnerCount])

  // 筹码守恒自检：算错了要当场看见，而不是等玩家自己发现少了一笔
  const checkedRef = useRef<string | null>(null)
  useEffect(() => {
    if (handId === null || checkedRef.current === handId) return
    checkedRef.current = handId
    const bad = checkChipConservation(view)
    if (bad !== null) showToast(bad, 'error', 0)
  }, [handId, view, showToast])

  return { summary, shown, ended: status === 'ended' }
}

/** 舞台顶部的结算卡，可收起查看完整牌桌。摊牌的牌力对比直接在第一屏，不再需要切标签页。 */
export function HandResultOverlay(): ReactNode {
  const { summary, shown } = useSettledHand()
  const veiled = useUiStore((s) => s.veiled)
  const view = useTableStore(selectView)
  const seats = useTableStore((s) => s.seats)
  if (summary === null || !shown || veiled) return null

  return <SettlementCard key={view?.handId} summary={summary} view={view} seats={seats} />
}

/**
 * 摊牌那一手，牌力对比直接在第一屏，不再需要切「单局比牌力」标签页才看得到——
 * 以前藏在第二个标签背后，很多人摊完牌不知道点哪里，只看见自己赢了多少、
 * 不知道赢在哪张牌上。弃牌获胜没有牌力可比，维持原来的「赢家 + 金额」列表。
 *
 * `seats` 由调用方传入而不是自己读 store：这是唯一需要玩家名字的地方，
 * 显式传参让这个组件能脱离 store 单独渲染测试（`useTableStore` 的 hook 在
 * `renderToStaticMarkup` 下不反映运行时状态，只有直接 props 能测）。
 *
 * Present only public result data; awarded amounts are not net profit.
 */
export function SettlementCard({
  summary,
  view = null,
  seats = [],
}: {
  readonly summary: HandSummary
  readonly view?: PublicHandState | null
  readonly seats?: readonly TableSeatInfo[]
}): ReactNode {
  const [minimized, setMinimized] = useState(false)
  if (minimized) return <button type="button" className="result-reopen" onClick={() => setMinimized(false)}>查看本手结果 ↗</button>

  const showdown = summary.endedBy === 'showdown' && view?.result ? compareShowdown(view.result.showdown) : null

  return (
    <section className="result" aria-label={`第 ${summary.handNo} 手结算`}>
      <div className="result__head">
        <span>第 {summary.handNo} 手</span><button type="button" className="result__close" aria-label="收起结算，查看牌桌" onClick={() => setMinimized(true)}>×</button>
      </div>
      <div className="result__total"><span>派奖</span><strong className="num">{formatChips(summary.awardedTotal)}</strong></div>

      {showdown !== null && view?.result ? <>
        {/* 公共牌先摆出来：牌力大小是「底牌 + 公共牌」一起看的，不给公共牌，比大小就得靠记 */}
        <div className="compare__board" aria-label="公共牌">{view.result.board.map((card,i) => <Card key={i} card={card} faceUp size="board" width={30} height={42} />)}</div>
        <div className="result__sd">
          {showdown.map(({entry,place}) => {
            const name = seats.find(s => s.seat === entry.seat)?.name ?? `座位 ${entry.seat + 1}`
            const award = summary.winners.find(w => w.seat === entry.seat)
            return (
              <div className="result__sd-row" key={entry.seat}>
                <span className="result__sd-place" data-place={place === null ? 'none' : place === 1 ? 'first' : 'other'}>
                  {place === null ? '—' : `#${place}`}
                </span>
                <div className="result__sd-cards" aria-label={`${name} 的底牌`}>
                  {entry.mucked
                    ? [0, 1].map((i) => <Card key={i} card={null} faceUp={false} size="board" width={26} height={36} />)
                    : entry.holeCards.map((card,i) => <Card key={i} card={card} faceUp size="board" width={26} height={36} />)}
                </div>
                <div className="result__sd-info">
                  <span className="result__name" title={name}>{name}</span>
                  {!entry.mucked && <span className="result__hand">{handLabel(entry.rank)}</span>}
                </div>
                {award && <span className="result__amount num result__sd-amount">+{formatChips(award.amount)}</span>}
              </div>
            )
          })}
        </div>
      </> : <>
      <div className="result__winners">
        {summary.winners.map((w) => (
          <div className="result__row" key={w.seat}>
            <span className="result__avatar" aria-hidden="true">{w.name.slice(0, 1)}</span>
            <div className="result__identity"><span className="result__name" title={w.name}>{w.name}</span>
              <span className="result__hand">{w.handText ?? '其他玩家弃牌'}</span>
            </div>
            <span className="result__amount num">+{formatChips(w.amount)}</span>
          </div>
        ))}
      </div>
      </>}

      {summary.uncalledReturn !== null && (
        <div className="result__note">
          另退还 {summary.uncalledReturn.name} 未被跟注的 {formatChips(summary.uncalledReturn.amount)} 筹码
        </div>
      )}
    </section>
  )
}

/**
 * dock 区的收尾条。牌局还能继续就是「下一手」，打不下去了就列名次 + 回大厅。
 * AppRoot 只在 `view.result !== null` 时挂载它，所以这里不再判一遍手牌相位。
 */
export function HandEndDock(): ReactNode {
  const { shown, ended } = useSettledHand()
  const seats = useTableStore((s) => s.seats)

  if (!shown) return <div className="adock adock--idle" />

  if (ended) {
    const ranked = [...seats].sort((a, b) => b.stack - a.stack || a.seat - b.seat)
    return (
      <div className="endbar">
        <div className="endbar__title">最终筹码</div>
        <div className="endbar__list">
          {ranked.map((s, i) => (
            <div className="endbar__row" key={s.seat}>
              <span className="endbar__rank">{i + 1}</span>
              <span className="result__name">{s.name}</span>
              <span className="result__amount num">{formatChips(s.stack)}</span>
            </div>
          ))}
        </div>
        <button type="button" className="result__cta" onClick={() => leaveLocalGame()}>
          返回大厅
        </button>
      </div>
    )
  }

  return (
    <div className="endbar">
      <p className="endbar__caption">好牌值得回味，下一手继续。</p>
      <button type="button" className="result__cta result__cta--big" onClick={() => nextLocalHand()}>
        下一手 <span aria-hidden="true">↗</span>
      </button>
    </div>
  )
}

export default HandResultOverlay
